<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { useApiClient } from "@/api/client";
import dayjs from "dayjs";
import { formatNumber } from "chartjs-graph";
import { useBaseStore } from "@/stores/base";
import { colorForFactor } from "@/modules/booking_experts/utils";
import BookingCurveModal from "@/modules/booking_experts/pages/Components/Modals/BookingCurveModal.vue";

const baseStore = useBaseStore();

watch(
	() => baseStore.selectedBexAdministration?.id,
	() => {
		loadBookings({
			page: selectedPage.value,
			itemsPerPage: selectedItemsPerPage.value,
			sortBy: [],
		});
	}
);

type BookingDTO = Modules.BookingExperts.Data.BookingDTO;

const api = useApiClient("bookingExperts");

const bookings = ref<BookingDTO[]>([]);
const bookingIndex = ref<number | null>(null);
const selectedBooking = computed(
	() => bookings.value[bookingIndex.value] ?? null
);
const showBooking = ref(false);

watch(
	selectedBooking,
	() => (showBooking.value = selectedBooking.value !== null)
);
watch(showBooking, () => {
	if (showBooking.value === false) {
		bookingIndex.value = null;
	}
});

const isLoading = ref(false);
const total = ref(200);
const search = ref("");
const selectedItemsPerPage = ref(25);
const selectedPage = ref(1);

type HeaderType = {
	title: string;
	key: string;
	align?: "start" | "end";
	sortable?: boolean;
};

const headers: HeaderType[] = [
	{
		title: "Booking ID",
		align: "start",
		key: "lodging_booking",
		sortable: false,
	},
	{
		title: "Rentable",
		align: "start",
		key: "lodging_type",
		sortable: false,
	},
	{
		title: "Pricelist",
		align: "start",
		key: "pricing_master_price_list_name",
		sortable: false,
	},
	{
		title: "Reservation Time",
		align: "start",
		key: "period_validity_date_time_fr",
		sortable: false,
	},
	{
		title: "Revenue (Ex VAT)",
		align: "start",
		key: "revenue_ex_vat",
		sortable: false,
	},
	{
		title: "Pricing Factor",
		align: "start",
		key: "pricing_factor_applied",
		sortable: false,
	},
	{
		title: "Arrival Date",
		align: "start",
		key: "period_subject_date_time_fr",
		sortable: false,
	},
	{
		title: "Departure Date",
		align: "start",
		key: "period_subject_date_time_to",
		sortable: false,
	},
	{
		title: "Status",
		align: "start",
		key: "lodging_status",
		sortable: false,
	},
];

const loadBookings = async ({ page, itemsPerPage: per_page, sortBy }) => {
	isLoading.value = true;
	selectedItemsPerPage.value = per_page;
	selectedPage.value = page;

	const sort = sortBy[0]
		? (sortBy[0]["order"] === "desc" ? "-" : "") + sortBy[0]["key"]
		: undefined;

	per_page = per_page === -1 ? 500 : per_page;

	const response = await api.getBookings({
		page,
		per_page,
		sort,
		"filter[administration_id]": baseStore.selectedBexAdministration?.id,
	});
	if (response.isOk()) {
		bookings.value = response.value.data;
		total.value = response.value.total;
	}

	isLoading.value = false;
};

function isIndexOutOfBounds(index: number) {
	return (
		(selectedPage.value - 1) * selectedItemsPerPage.value + index >=
			total.value ||
		(selectedPage.value <= 1 && index < 0)
	);
}

function isBookingValid(index: number) {
	const booking = bookings.value[index] ?? null;
	return booking.pricing_factor_applied !== null;
}

// Because of reactivity shenanigans, this won't skip an invalid booking if it's the first one on a new page
// Not too big of a deal I reckon
function changeBookingIndex(delta: number) {
	if (bookingIndex.value === undefined) return;

	isLoading.value = true;
	let index = bookingIndex.value;
	for (let counter = 0; counter < 1000; counter++) {
		index += delta;
		if (isIndexOutOfBounds(index)) {
			break;
		}
		if (index >= selectedItemsPerPage.value) {
			index = 0;
			selectedPage.value++;
		} else if (index < 0) {
			index = selectedItemsPerPage.value - 1;
			selectedPage.value--;
		}
		if (isBookingValid(index)) {
			bookingIndex.value = index;
			break;
		}
	}
	isLoading.value = false;
}
</script>

<template>
	<booking-curve-modal
		v-if="showBooking"
		v-model:visible="showBooking"
		:show-date-control="true"
		:booking="selectedBooking"
		@day:plus="changeBookingIndex(1)"
		@day:minus="changeBookingIndex(-1)"
	/>
	<v-card class="mt-5 px-5 py-3">
		<v-data-table-server
			:headers="headers"
			:items="bookings"
			:items-per-page="selectedItemsPerPage"
			:page="selectedPage"
			:items-length="total"
			@update:options="loadBookings"
			:loading="isLoading"
			:search="search"
		>
			<template v-slot:item.lodging_booking="{ item, index }">
				<span
					@click="bookingIndex = index"
					class="text-decoration-underline cursor-pointer"
				>
					{{ item.lodging_booking }}
				</span>
			</template>
			<template v-slot:item.lodging_type="{ item }">
				<v-chip rounded="sm" size="small" variant="flat">
					<span>
						{{
							item.lodging_type.length > 30
								? item.lodging_type.substring(0, 30) + "…"
								: item.lodging_type
						}}
					</span>
					<v-tooltip
						v-if="item.lodging_type.length > 30"
						activator="parent"
						location="top"
					>
						{{ item.lodging_type }}
					</v-tooltip>
				</v-chip>
			</template>
			<template v-slot:item.pricing_master_price_list_name="{ item }">
				<v-chip rounded="sm" size="small" variant="flat">
					<span>
						{{
							item.pricing_master_price_list_name.length > 30
								? item.pricing_master_price_list_name.substring(0, 30) + "…"
								: item.pricing_master_price_list_name
						}}
					</span>
					<v-tooltip
						v-if="item.pricing_master_price_list_name.length > 30"
						activator="parent"
						location="top"
					>
						{{ item.pricing_master_price_list_name }}
					</v-tooltip>
				</v-chip>
			</template>
			<!--			<template v-slot:item.pricing_model_applied_name="{ item }">-->
			<!--				<v-chip rounded="sm" size="small" variant="tonal">-->
			<!--					<span v-if="item.pricing_model_applied_id">-->
			<!--						{{ item.pricing_model_applied_name }}-->
			<!--					</span>-->
			<!--					<span v-else>None</span>-->
			<!--				</v-chip>-->
			<!--			</template>-->
			<template v-slot:item.revenue_ex_vat="{ item }">
				{{ formatNumber(item.revenue_ex_vat, "money") }}
			</template>
			<template v-slot:item.period_validity_date_time_fr="{ item }">
				{{ dayjs(item.period_validity_date_time_fr).format("D MMM HH:mm") }}
			</template>
			<template v-slot:item.period_subject_date_time_fr="{ item }">
				{{ dayjs(item.period_subject_date_time_fr).format("D MMM") }}
			</template>
			<template v-slot:item.period_subject_date_time_to="{ item }">
				{{ dayjs(item.period_subject_date_time_to).format("D MMM") }}
			</template>
			<template v-slot:item.pricing_factor_applied="{ item }">
				<v-chip
					v-if="
						item.pricing_factor_applied !== null &&
						item.pricing_factor_applied !== undefined
					"
					rounded="sm"
					density="compact"
					label
					variant="flat"
					:color="colorForFactor(item.pricing_factor_applied)"
				>
					{{ item.pricing_factor_applied.toFixed(2) }}
				</v-chip>
				<v-chip v-else density="compact" class="neutral" disabled label>
					-
				</v-chip>
			</template>
		</v-data-table-server>
	</v-card>
</template>
